import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import useGlobalBarcodeScanner from "@util/hook/useGlobalBarcodeScanner";
import { callRef } from "@util/utils";

/**
 * SkuAddToListContext - Context 定義
 */
const SkuAddToListContext = createContext(null);

export const useSkuAddToListContext = () => {
	const context = useContext(SkuAddToListContext);
	if (!context) {
		throw new Error("SkuAddToList 的子組件必須在 SkuAddToList 內部使用");
	}
	return context;
};

/**
 * SkuAddToListProvider - UI 層 + 業務邏輯引用
 *
 * 職責：
 * - UI：inputKey、clearInput、showScanDialog、handleOpenScanDialog、handleCloseScanDialog、handleScanSuccess、handleScanError
 * - 整合 useGlobalBarcodeScanner、getInputValue、handleSubmit
 * - 包裝 handleAddProduct：成功加入後 clearInput
 */
export const SkuAddToListProvider = ({
	children,
	inputRef,
	enabled = true,
	// 來自 useSkuAddToList（業務邏輯）
	products = [],
	handleAddProduct,
	handleQuantityChange,
	handleDeleteProduct,
	quantityEditTarget,
	handleOpenQuantityEdit,
	handleConfirmQuantity,
	handleCloseQuantityEdit,
	getProductKey,
	isAdding,
}) => {
	const [inputKey, setInputKey] = useState(0); // 清空 input 的 key
	const [showScanDialog, setShowScanDialog] = useState(false);

	// 清空 input：改變 key 讓 Input 重置
	const clearInput = useCallback(() => {
		setInputKey((prev) => prev + 1);
	},
		// eslint-disable-next-line
		[]);

	// 掃描對話框控制
	const handleOpenScanDialog = useCallback(() => setShowScanDialog(true), []);

	// 不在此處關閉視窗，由 BarcodeScannerDialog 依 continuousScan 決定是否關閉
	const handleCloseScanDialog = useCallback(() => setShowScanDialog(false), []);

	// 從 inputRef 取得輸入值，優先使用 getResult 方法（BarcodeScannerDialog 會提供），沒有則回傳空字串
	const getInputValue = useCallback(() => {
		const result = callRef(inputRef, "getResult");
		return typeof result === "string" ? result : "";
	},
		// eslint-disable-next-line
		[]);

	// 包裝 handleAddProduct：成功加入後清空 Input
	const handleAddProductWithClear = useCallback(
		async (sku) => {
			const success = await handleAddProduct(sku);
			if (success) clearInput();
		},
		// eslint-disable-next-line
		[handleAddProduct, clearInput]
	);

	// 提交輸入框內容：呼叫 handleAddProductWithClear，成功加入後清空 Input
	const handleSubmit = useCallback(() => {
		handleAddProductWithClear(getInputValue());
	},
		// eslint-disable-next-line
		[getInputValue, handleAddProductWithClear]);

	// 掃描成功：呼叫 handleAddProductWithClear，成功加入後清空 Input；不在此處關閉視窗，由 BarcodeScannerDialog 依 continuousScan 決定是否關閉
	const handleScanSuccess = useCallback(
		async (sku) => {
			if (!sku || !sku.trim()) return;
			// 不在此處關閉視窗，由 BarcodeScannerDialog 依 continuousScan 決定是否關閉
			await handleAddProductWithClear(sku.trim());
		},
		// eslint-disable-next-line
		[handleAddProductWithClear]
	);

	// 掃描錯誤：目前僅 log，未額外處理
	const handleScanError = useCallback((err) => {
		console.error("商品掃描錯誤:", err);
	},
		// eslint-disable-next-line
		[]);

	// 整合全局條碼掃描器：當有新掃描結果且條件允許時，呼叫 handleAddProductWithClear；條件為 enabled 為 true 且未開啟掃描對話框（避免與 BarcodeScannerDialog 衝突）
	const { lastScannedCode, resetScannedCode } = useGlobalBarcodeScanner({
		targetRef: inputRef,
		threshold: 500,
		terminatorKey: "Enter",
		enabled: enabled && !showScanDialog,
	});

	// 當 lastScannedCode 更新且條件允許時，呼叫 handleAddProductWithClear 並重置掃描碼；條件同上
	useEffect(() => {
		if (lastScannedCode && enabled && !showScanDialog) {
			handleAddProductWithClear(lastScannedCode);
			resetScannedCode();
		}
		// eslint-disable-next-line
	}, [lastScannedCode, enabled, showScanDialog, handleAddProductWithClear, resetScannedCode]);

	const contextValue = {
		inputKey,
		showScanDialog,
		isAdding,
		products,
		quantityEditTarget,
		handleOpenQuantityEdit,
		getInputValue,
		clearInput,
		handleAddProduct: handleAddProductWithClear,
		handleSubmit,
		handleOpenScanDialog,
		handleCloseScanDialog,
		handleScanSuccess,
		handleScanError,
		handleQuantityChange,
		handleDeleteProduct,
		handleConfirmQuantity,
		handleCloseQuantityEdit,
		inputRef,
		getProductKey,
	};

	return <SkuAddToListContext.Provider value={contextValue}>{children}</SkuAddToListContext.Provider>;
};

SkuAddToListProvider.propTypes = {
	children: PropTypes.node.isRequired,
	inputRef: PropTypes.object,
	enabled: PropTypes.bool,
	products: PropTypes.array,
	handleAddProduct: PropTypes.func,
	handleQuantityChange: PropTypes.func,
	handleDeleteProduct: PropTypes.func,
	quantityEditTarget: PropTypes.object,
	handleOpenQuantityEdit: PropTypes.func,
	handleConfirmQuantity: PropTypes.func,
	handleCloseQuantityEdit: PropTypes.func,
	getProductKey: PropTypes.func,
	isAdding: PropTypes.bool,
};
